<template>
  <section class="services">
    <div class="container">
      <div class="section-header">
        <h2 class="section-title">Capabilities & Applications</h2>
        <p class="section-description">
          Advanced manufacturing capabilities delivering precision ceramic capacitors 
          for demanding applications across multiple industries.
        </p>
      </div>
      
      <div class="services-grid">
        <div 
          v-for="service in services" 
          :key="service.id"
          class="service-card"
          @mouseover="playHoverSound"
        >
          <div class="service-icon">
            <i :class="service.icon"></i>
          </div>
          <h3 class="service-title">{{ service.title }}</h3>
          <p class="service-description">{{ service.description }}</p>
          <ul class="service-features">
            <li v-for="feature in service.features" :key="feature">
              {{ feature }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'

interface Service {
  id: number
  title: string
  description: string
  icon: string
  features: string[]
}

const services = ref<Service[]>([
  {
    id: 1,
    title: 'High-Volume Manufacturing',
    description: 'State-of-the-art production facilities with automated processes and rigorous quality control.',
    icon: '⚙️',
    features: [
      'Automated Production Lines',
      'ISO 9001:2015 Certified',
      'Real-time Quality Monitoring',
      'Rapid Prototyping',
      'Custom Tooling'
    ]
  },
  {
    id: 3,
    title: 'Custom Engineering',
    description: 'Expert engineering support for application-specific capacitor solutions.',
    icon: '🛠️',
    features: [
      'Design Consultation',
      'Material Selection',
      'Custom Specifications',
      'Simulation & Modeling',
      'Application Support'
    ]
  },
  {
    id: 4,
    title: 'Industry Applications',
    description: 'Specialized solutions for diverse industry requirements and certifications.',
    icon: '🏭',
    features: [
      'Automotive AEC-Q200',
      'Medical IEC 60601',
      'Aerospace MIL-PRF',
      'Industrial Equipment',
      'Consumer Electronics'
    ]
  }
])

function playHoverSound() {
  // Placeholder for hover interaction
}
</script>

<style scoped>
.services {
  padding: 6rem 0;
  background: var(--color-background-soft);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.section-header {
  text-align: center;
  max-width: 600px;
  margin: 0 auto 4rem;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  color: var(--color-heading);
}

.section-description {
  font-size: 1.2rem;
  line-height: 1.6;
  color: var(--color-text);
  margin: 0;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.service-card {
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.service-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(244, 196, 48, 0.1), transparent);
  transition: left 0.5s ease;
}

.service-card:hover::before {
  left: 100%;
}

.service-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  border-color: var(--color-primary);
}

.service-icon {
  font-size: 3rem;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.service-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  color: var(--color-heading);
}

.service-description {
  font-size: 1rem;
  line-height: 1.6;
  color: var(--color-text);
  margin: 0 0 1.5rem 0;
}

.service-features {
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
}

.service-features li {
  padding: 0.5rem 0;
  color: var(--color-text);
  border-bottom: 1px solid var(--color-border);
  position: relative;
  padding-left: 1.5rem;
}

.service-features li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: var(--color-primary);
  font-weight: bold;
}

.service-features li:last-child {
  border-bottom: none;
}

@media (max-width: 768px) {
  .services {
    padding: 4rem 0;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .section-description {
    font-size: 1.1rem;
  }
  
  .services-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .service-card {
    padding: 1.5rem;
  }
}
</style>
